import { Component, OnInit, ViewChild } from '@angular/core';
import { StaffDTO, TTMap, SearchByOptions, StaffCodeDTO } from 'src/store/workspace/workspace.model';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from 'src/store';
import * as fromWorkspace from 'src/store/workspace';
import { FormGroup, FormBuilder } from '@angular/forms';
import { GridComponent, PagerSettings, PageChangeEvent } from '@progress/kendo-angular-grid';
import { isNumber, cloneDeep } from 'lodash';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';

@Component({
  selector: 'app-staff',
  templateUrl: './staff.component.html',
  styleUrls: ['./staff.component.scss'],
})
export class StaffComponent implements OnInit {
  gridData$: Observable<[StaffDTO[], number | 'all', SearchByOptions]>;
  gridData: StaffDTO[];
  gridSubs: Subscription;
  targetStaff: StaffDTO;
  targetFirstName: string;
  targetLastName: string;
  deleteOpened: boolean;
  newUpdateOpened: boolean;

  staff$: Observable<TTMap<StaffDTO>>;
  staffMap: TTMap<StaffDTO>;
  staffSubs: Subscription;
  staff: StaffDTO[];
  allStaff: StaffDTO[];
  staffTypes: StaffCodeDTO[];

  staffCodes$: Observable<StaffCodeDTO[]>;
  staffCodesSub: Subscription;
  staffCodes: StaffCodeDTO[];

  roles: StaffCodeDTO[];
  mainRoleTypes: StaffCodeDTO[];

  userBlank: boolean;
  emailBlank: boolean;
  ptoDays: number[] = [0, 5, 10, 15, 20];
  title: string;
  button: string;
  confirmUsernameChanged: boolean;
  tempUsername: string;
  formGroup: FormGroup;

  public pageSize: number | 'all' = 'all';
  public skip = 0;
  public length: number;
  public pageable: PagerSettings = {
    pageSizes: true,
    previousNext: true,
    info: true
  };
  public pageSizes: any[] = [10, 20, 30, 40, 50, {
    text: 'All',
    value: 'all'
  }];

  public sort: SortDescriptor[];
  public sortable = {
    allowUnsort: true,
    mode: 'single'
  };

  @ViewChild('grid', {
    static: false
  }) grid: GridComponent;

  public columns: any[] = [{
    field: 'staffID',
    hidden: true
  },
  {
    field: 'active',
    title: 'Active',
  },
  {
    field: 'username',
    title: 'Username'
  },
  {
    field: 'firstname',
    title: 'First Name'
  },
  {
    field: 'lastname',
    title: 'Last Name'
  },
  {
    field: 'emailAddress',
    title: 'Email Address'
  },
  {
    field: 'staffType',
    title: 'Staff Type'
  },
  {
    field: 'mainRoleType',
    title: 'Role'
  }];

  constructor(private store: Store<fromRoot.AppState> , private formBuilder: FormBuilder) {
    this.store.dispatch(new fromWorkspace.LoadStaffs());
    this.store.dispatch(new fromWorkspace.LoadStaffPageSize());
    this.sort = [];
    this.gridData$ = combineLatest(
      this.store.select(state => Object.values(state.workspace.staffs)),
      this.store.select(state => state.workspace.staffPageSize),
      this.store.select(state => state.workspace.searchByOptions)
    );
    this.staff$ = this.store.select(state => state.workspace.staffs);
    this.staffSubs = this.staff$.subscribe(staff => {
      this.staffMap = staff;
      this.staff = Object.values(staff).sort((a, b) => {
        if (a.username.toLowerCase() > b.username.toLowerCase()) {
          return 1;
        }
        if (a.username.toLowerCase() < b.username.toLowerCase()) {
          return -1;
        }
        return 0;
      });
    });
    this.staffCodes$ = this.store.select(state => state.workspace.staffCodes);
    this.staffCodesSub = this.staffCodes$.subscribe(codes => {
      this.staffCodes = codes;

      // staff type display codes
      const staffTypeDescription = "Staff Classification (W2, 1099)";
      this.staffTypes = codes.filter(staffCode => staffCode.description === staffTypeDescription);

      // role display codes
      const roleDescription = "Permission Role Type";
      this.roles = codes.filter(staffCode => staffCode.description === roleDescription);

      // main role type display codes
      const mainRoleTypeDescription = "Main Role Type of Staff Member";
      this.mainRoleTypes = codes.filter(staffCode => staffCode.description === mainRoleTypeDescription);
    });

    this.gridSubs = this.gridData$.subscribe(([staff, pageSize, searchByOptions]) => {
      if (searchByOptions.staff && searchByOptions.staff.staffID) {
        staff = staff.filter(s => s.staffID === searchByOptions.staff.staffID);
      }
      staff = staff.map(s => {
        const staff = cloneDeep(s);
        staff.staffName = this.staffMap[staff.staffID].staffName;
        return staff;
      });
      staff = orderBy(staff, this.sort);
      this.length = staff.length;
      this.pageSize = pageSize;
      if (!isNumber(pageSize) && this.length) {
        this.pageSize = this.length || 10;
      }
      this.allStaff = staff;
      const size = this.pageSize as number;
      this.gridData = staff.slice(this.skip, this.skip + size);
    });
    this.store.dispatch(new fromWorkspace.LoadStaffPageSize());
    this.store.select(state => state.workspace.staffPageSize).subscribe(size => {
      this.pageSize = size;
    });
  }

  ngOnInit() {}

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.store.dispatch(new fromWorkspace.LoadStaffs());
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    let pageSize: number | 'all' = event.take;
    if (pageSize === this.length) {
      pageSize = 'all';
    }

    this.store.dispatch(new fromWorkspace.SetStaffPageSize(pageSize));
    this.store.dispatch(new fromWorkspace.LoadStaffs());
    this.store.select(state => state.workspace.staffPageSize);
  }

  public addStaff(): void {
    const dataItem: StaffDTO = {
      staffID: 0,
      username: "",
      staffName: "",
      staffType: "W2",
      corpToCorp: false,
      corpName: "",
      active: true,
      emailAddress: "",
      userRole: 2,
      timeOffAllowed: 0,
      employStartDate: null,
      employTermDate: null,
      mainRoleType: "",
      partTime: false,
      hourly: false,
      hourlyExpectedHours: null,
      isCurrentUserAdmin: false,
      isCurrentUser: false, 
    };
    this.title = "New Staff";
    this.button = "Add";
    this.targetStaff = dataItem;
    this.userBlank = true;
    this.emailBlank = true;
    this.targetFirstName = this.getFirstName(dataItem.staffName);
    this.targetLastName = this.getLastName(dataItem.staffName);
    this.newUpdateOpened = true;
  }

  public deleteStaff(dataItem): void {
    this.targetStaff = dataItem;
    this.deleteOpened = true;
  }

  public editStaff(dataItem): void {
    this.title = "Edit Staff";
    this.button = "Update";
    this.targetStaff = dataItem;
    this.userBlank = false;
    this.emailBlank = false;
    if(this.targetStaff.employTermDate != null || this.targetStaff.employTermDate != undefined)
    {
      this.targetStaff.employTermDate = new Date(this.targetStaff.employTermDate);
    }
    if(this.targetStaff.employStartDate != null || this.targetStaff.employStartDate != undefined)
    {
      this.targetStaff.employStartDate = new Date(this.targetStaff.employStartDate);
    }
    this.targetFirstName = this.getFirstName(dataItem.staffName);
    this.targetLastName = this.getLastName(dataItem.staffName);
    this.newUpdateOpened = true;
  }

  public delete(dataItem: StaffDTO) {
    this.deleteOpened = false;
    this.store.dispatch(new fromWorkspace.DeleteStaff(dataItem));
    this.targetStaff = null;
  }

  public addUpdate(dataItem: StaffDTO) {
    this.newUpdateOpened = false;
    this.targetStaff.staffName = this.targetFirstName + ' ' + this.targetLastName;
    this.store.dispatch(dataItem.staffID ? new fromWorkspace.UpdateStaff(dataItem) : new fromWorkspace.AddStaff(dataItem));
    this.targetStaff = null;
  }

  public cancel(){
    this.targetStaff = null;
    this.targetFirstName = null;
    this.targetLastName = null;
    this.deleteOpened = false;
    this.newUpdateOpened = false;
  }

  public flipActiveFlag(staff: StaffDTO) {
    const s = cloneDeep(staff);
    this.store.dispatch(new fromWorkspace.UpdateStaff(s));
  }

  public getFirstName(staffName: string) {
    const name = staffName.split(' ');
    return name[0];
  }

  public getLastName(staffName: string) {
    const name = (staffName.split(' ').slice(1).join(" "));
    return name;
  }

  public getSelectedMainRoleType() {
    return this.mainRoleTypes.find(mainRoleType => mainRoleType.timeTrackerCode === this.targetStaff.mainRoleType);
  }

  public getSelectedUserRole() {
    return this.roles.find(userRole => parseInt(userRole.timeTrackerCode) === this.targetStaff.userRole);
  }

  public getSelectedStaffType() {
    return this.staffTypes.find(staffType => staffType.timeTrackerCode === this.targetStaff.staffType);
  }

  setUsernameEmail() {
    const name = (this.targetFirstName.split('')[0] + this.targetLastName.replace(/[^a-zA-Z0-9]/ig,"")).toLowerCase();
    const email = name + '@tsp-consulting.com';
    if(this.userBlank)
    {
      this.targetStaff.username = name;
    }
    if(this.emailBlank){
      
      this.targetStaff.emailAddress = email;
    }

    if(!this.userBlank && !this.emailBlank && this.targetStaff.username != name )
    {
        this.tempUsername = name;
        this.confirmUsernameChanged = true;
    }
  }

  usernameChanged() {
    this.userBlank = false;
  }

  emailChanged() {
    this.emailBlank = false;
  }

  isW2() {
    return (this.targetStaff.staffType == "W2");
  }

  isHourly() {
    return (!this.targetStaff.hourly);
  }

  changeUsername() {
    const name = this.tempUsername;
    const email = this.tempUsername + '@tsp-consulting.com';
    this.targetStaff.username = name;
    this.targetStaff.emailAddress = email;
    this.confirmUsernameChanged = false;
    this.tempUsername = null;
  }

  cancelUsernameChange() {
    this.confirmUsernameChanged = false;
    this.tempUsername = null;
  }

}
